import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";
import useAuth from "../../hooks/useAuth";
import { useNotification } from "../../hooks/useNotification";
import { useZoomSettings } from "../../hooks/useZoomSettings";
import { notificationModalOpenState } from "../../recoil/notification";
import { isSpecialUser } from "../../utils/environment";
import Dashboard from "./tabs/dashboard/Dashboard";
import DashboardMobile from "./tabs/dashboard/DashboardMobile";
import MyAdvertisement from "./tabs/myAdvertisement/MyAdvertisement";
import Settings from "./tabs/Settings";
import Deposit from "./tabs/deposit/Deposit";
import AddAdvertisement from "./tabs/addAdvertisement/AddAdvertisement";
import CheckerManagement from "./tabs/checkerManagement/CheckerManagement";
import RankKeyword from "./tabs/rankKeyword/RankKeyword";
import NotificationModal from "../../components/NotificationModal";
import FAQModal from "../../components/FAQModal";
import { MdQueryStats } from "react-icons/md";
import menuIcon from "../../assets/icons/menu_m.svg";
import logoIcon from "../../assets/images/logo.png";
import bellIcon from "../../assets/images/bell.png";
import dashboardIcon from "../../assets/icons/deshboard.svg";
import dashboardIconActive from "../../assets/icons/deshboardActive.svg";
import advertismentIcon from "../../assets/icons/advertismentIcon.svg";
import advertismentIconActive from "../../assets/icons/advertismentIconActive.svg";
import checkAdvertisementIcon from "../../assets/icons/checkAdvertisement.svg";
import checkAdvertisementIconActive from "../../assets/icons/checkAdvertisementActive.svg";
import balanceIcon from "../../assets/icons/balance.svg";
import balanceIconActive from "../../assets/icons/balanceActive.svg";
import bookIcon from "../../assets/icons/book.svg";
import infoIcon from "../../assets/icons/info.svg";
import kakaotalkIcon from "../../assets/icons/kakaotalk.svg";
import "./HomeMobile.css";

const HomeMobile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 디버깅: user 객체 확인
  useEffect(() => {
    console.log("HomeMobile - user:", user);
    console.log("HomeMobile - user?.role:", user?.role);
    console.log("HomeMobile - is AGENCY?", user?.role === "AGENCY");
  }, [user]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [depositSubTab, setDepositSubTab] = useState("chargeRequest");
  const [tempDepositSubTab, setTempDepositSubTab] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // 알림 관련
  const { unreadCount, fetchNotifications } = useNotification();
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useRecoilState(
    notificationModalOpenState,
  );
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);

  // Zoom Settings 관련
  const { settings } = useZoomSettings();

  // URL 파라미터에서 원하는 탭 확인
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const desiredTab = params.get("tab");
    const desiredSubTab = params.get("subTab");

    if (
      desiredTab &&
      [
        "dashboard",
        "users",
        "settings",
        "deposit",
        "addAdvertisement",
        "myAdvertisement",
        "checkerManagement",
        "rankKeyword",
      ].includes(desiredTab)
    ) {
      // CHECKER 역할은 대시보드, 광고 등록, 예치금 관리 접근 불가
      if (user?.role === "CHECKER") {
        if (
          desiredTab === "dashboard" ||
          desiredTab === "addAdvertisement" ||
          desiredTab === "deposit"
        ) {
          // 접근 불가능한 탭이면 광고 확인으로 리다이렉트
          setActiveTab("myAdvertisement");
          navigate("/?tab=myAdvertisement", { replace: true });
          return;
        }
      }

      setActiveTab(desiredTab);

      // 서브탭이 있는 경우 처리
      if (desiredSubTab && desiredTab === "deposit") {
        setTempDepositSubTab(desiredSubTab);
      }
    } else {
      // URL 파라미터가 없으면 기본 탭 설정
      // CHECKER 역할은 광고 확인이 기본, 그 외는 대시보드
      const defaultTab =
        user?.role === "CHECKER" ? "myAdvertisement" : "dashboard";
      setActiveTab(defaultTab);
      navigate(`/?tab=${defaultTab}`, { replace: true });
    }
  }, [location.search, navigate, user?.role]);

  const handleLogout = async () => {
    // logout() 함수가 이미 window.location.replace로 리다이렉트하므로
    // navigate는 호출하지 않음 (중복 리다이렉트 방지)
    setIsSidebarOpen(false);
    await logout();
    navigate("/login");
  };

  const handleGuideClick = () => {
    alert("이용 가이드 링크를 준비 중입니다.");
  };

  const handleFAQClick = () => {
    setIsSidebarOpen(false);
    setIsFAQModalOpen(true);
  };

  const handleKakaoChat = () => {
    if (settings?.kakaoChannelId) {
      window.open(settings.kakaoChannelId, "_blank");
    } else {
      alert("카카오톡 상담 링크를 준비 중입니다.");
    }
  };

  const handleNotificationClick = async () => {
    await fetchNotifications(1);
    setIsNotificationModalOpen(true);
  };

  const handleTabClick = (tabName, subTab = null) => {
    // 모바일에서 탭 클릭 시 사이드바 닫기
    setIsSidebarOpen(false);

    // CHECKER 역할은 대시보드, 광고 등록, 예치금 관리 접근 불가
    if (user?.role === "CHECKER") {
      if (
        tabName === "dashboard" ||
        tabName === "addAdvertisement" ||
        tabName === "deposit"
      ) {
        return;
      }
    }

    // 대시보드는 로그인 없이도 접근 가능 (CHECKER 제외)
    if (tabName === "dashboard") {
      setActiveTab(tabName);
      navigate(`/?tab=${tabName}${subTab ? `&subTab=${subTab}` : ""}`);
      return;
    }

    // 예치금 관리 탭으로 이동하면서 서브탭 지정
    if (tabName === "deposit" && subTab) {
      setTempDepositSubTab(subTab);
    } else {
      setTempDepositSubTab(null);
    }

    setActiveTab(tabName);
    navigate(`/?tab=${tabName}${subTab ? `&subTab=${subTab}` : ""}`);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardMobile onTabChange={handleTabClick} />;
      case "addAdvertisement":
        return <AddAdvertisement onTabChange={handleTabClick} />;
      case "myAdvertisement":
        return <MyAdvertisement />;
      case "checkerManagement":
        return <CheckerManagement />;
      case "rankKeyword":
        return <RankKeyword />;
      case "settings":
        return <Settings />;
      case "deposit":
        return <Deposit initialTab={tempDepositSubTab || depositSubTab} />;
      default:
        return <Dashboard onTabChange={handleTabClick} />;
    }
  };

  const mobileTab = new URLSearchParams(location.search).get("tab");
  const isRankKeywordSeo = mobileTab === "rankKeyword";

  return (
    <div className="home-mobile-container">
      <Helmet>
        {isRankKeywordSeo ? (
          <>
            <title>
              올려줌 순위·키워드 | 스마트스토어 광고 순위·네이버 연관 키워드
              분석
            </title>
            <meta
              name="description"
              content="올려줌(olzoom.com 공식)에서 스마트스토어 간편 광고 순위와 네이버 연관 키워드 검색량·경쟁도·블로그 포화도를 한 화면에서 확인하세요. 셀프 마케팅 플랫폼."
            />
            <meta
              name="keywords"
              content="올려줌,olzoom,올려줌공식,스마트스토어순위,네이버광고순위,연관키워드,키워드분석,검색량,쇼핑순위"
            />
            <meta name="robots" content="index,follow" />
            <link
              rel="canonical"
              href="https://www.olzoom.com/?tab=rankKeyword"
            />
          </>
        ) : (
          <>
            <title>올려줌 - 모바일</title>
            <meta
              name="description"
              content="올려줌 모바일 - 셀프 마케팅 플랫폼"
            />
            <meta name="robots" content="noindex,nofollow" />
          </>
        )}
      </Helmet>

      {/* 헤더 */}
      <div className="home-mobile-header">
        <div className="home-mobile-header-left">
          <button
            className="home-mobile-hamburger-btn"
            onClick={() => setIsSidebarOpen(true)}
          >
            <img src={menuIcon} alt="메뉴" className="home-mobile-menu-icon" />
          </button>
        </div>
        <div className="home-mobile-header-center">
          <img src={logoIcon} alt="올려줌" className="home-mobile-logo" />
        </div>
        <div className="home-mobile-header-right">
          <button
            className="home-mobile-notification-btn"
            onClick={handleNotificationClick}
            style={{ position: "relative" }}
          >
            <img
              src={bellIcon}
              alt="알림"
              className="home-mobile-notification-icon"
            />
            {unreadCount > 0 && (
              <span className="home-mobile-notification-badge">
                {unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="home-mobile-main-content">{renderTabContent()}</div>

      {/* 사이드바 오버레이 */}
      <div
        className={`home-mobile-overlay ${isSidebarOpen ? "open" : ""}`}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* 모바일 사이드바 */}
      <div className={`home-mobile-sidebar ${isSidebarOpen ? "open" : ""}`}>
        {/* 사용자 정보 섹션 */}
        <div className="home-mobile-user-section">
          {user ? (
            <>
              <div className="home-mobile-user-info">
                {user?.price_grade?.name && (
                  <div className="home-mobile-grade-tag">
                    {user.price_grade.name}
                  </div>
                )}
                <span className="home-mobile-user-name">{user.name} 님</span>
              </div>

              {user?.deposit !== undefined && (
                <div className="home-mobile-deposit-info">
                  <span>예치금: {user.deposit.toLocaleString()} 원</span>
                </div>
              )}
            </>
          ) : (
            <div className="home-mobile-login-prompt">
              <span>로그인이 필요합니다</span>
              <button
                onClick={() => {
                  navigate("/login");
                  setIsSidebarOpen(false);
                }}
                className="home-mobile-login-btn"
              >
                로그인
              </button>
            </div>
          )}
        </div>

        {/* 네비게이션 메뉴 */}
        <nav className="home-mobile-nav">
          <button
            className={`home-mobile-nav-btn ${
              activeTab === "dashboard" ? "active" : ""
            } ${user?.role === "CHECKER" ? "disabled" : ""}`}
            onClick={() => handleTabClick("dashboard")}
            disabled={user?.role === "CHECKER"}
          >
            <img
              src={
                activeTab === "dashboard" ? dashboardIconActive : dashboardIcon
              }
              alt="대시보드"
              className="home-mobile-nav-icon"
            />
            <span>대시보드</span>
          </button>
          <button
            className={`home-mobile-nav-btn ${
              activeTab === "addAdvertisement" ? "active" : ""
            } ${!user || user?.role === "CHECKER" ? "disabled" : ""}`}
            onClick={() => handleTabClick("addAdvertisement")}
            disabled={!user || user?.role === "CHECKER"}
          >
            <img
              src={
                activeTab === "addAdvertisement"
                  ? advertismentIconActive
                  : advertismentIcon
              }
              alt="광고 등록"
              className="home-mobile-nav-icon"
            />
            <span>광고 등록</span>
          </button>
          <button
            className={`home-mobile-nav-btn ${
              activeTab === "myAdvertisement" ? "active" : ""
            } ${!user ? "disabled" : ""}`}
            onClick={() => handleTabClick("myAdvertisement")}
          >
            <img
              src={
                activeTab === "myAdvertisement"
                  ? checkAdvertisementIconActive
                  : checkAdvertisementIcon
              }
              alt="광고 확인"
              className="home-mobile-nav-icon"
            />
            <span>광고 확인</span>
          </button>
          <button
            className={`home-mobile-nav-btn ${
              activeTab === "deposit" ? "active" : ""
            } ${!user || user?.role === "CHECKER" ? "disabled" : ""}`}
            onClick={() => handleTabClick("deposit")}
            disabled={!user || user?.role === "CHECKER"}
          >
            <img
              src={activeTab === "deposit" ? balanceIconActive : balanceIcon}
              alt="예치금 관리"
              className="home-mobile-nav-icon"
            />
            <span>예치금 관리</span>
          </button>
          {/* 체커 관리 - SPECIAL_USER_IDS만 표시 */}
          {isSpecialUser(user?.id) && (
            <>
              <button
                className={`home-mobile-nav-btn ${
                  activeTab === "checkerManagement" ? "active" : ""
                }`}
                onClick={() => handleTabClick("checkerManagement")}
              >
                <img
                  src={
                    activeTab === "checkerManagement"
                      ? checkAdvertisementIconActive
                      : checkAdvertisementIcon
                  }
                  alt="체커 관리"
                  className="home-mobile-nav-icon"
                />
                <span>체커 관리</span>
              </button>
            </>
          )}
          <button
            type="button"
            className={`home-mobile-nav-btn ${
              activeTab === "rankKeyword" ? "active" : ""
            }`}
            onClick={() => handleTabClick("rankKeyword")}
          >
            <MdQueryStats className="home-mobile-nav-icon" />
            <span>순위&amp;키워드</span>
          </button>
        </nav>

        {/* 하단 메뉴 */}
        <div className="home-mobile-footer-menu">
          <button className="home-mobile-footer-btn" onClick={handleGuideClick}>
            <img
              src={bookIcon}
              alt="이용 가이드"
              className="home-mobile-footer-icon"
            />
            <span>이용 가이드</span>
          </button>
          <button className="home-mobile-footer-btn" onClick={handleFAQClick}>
            <img
              src={infoIcon}
              alt="자주묻는질문"
              className="home-mobile-footer-icon"
            />
            <span>자주 묻는 질문</span>
          </button>
          <button className="home-mobile-footer-btn" onClick={handleKakaoChat}>
            <img
              src={kakaotalkIcon}
              alt="카카오톡 상담하기"
              className="home-mobile-footer-icon"
            />
            <span>카카오톡 상담하기</span>
          </button>
        </div>

        {/* 로그아웃 버튼 */}
        {user && (
          <div className="home-mobile-logout-section">
            <button onClick={handleLogout} className="home-mobile-logout-btn">
              로그아웃
            </button>
          </div>
        )}
      </div>

      {/* 알림 모달 */}
      <NotificationModal />

      {/* FAQ 모달 */}
      <FAQModal
        isOpen={isFAQModalOpen}
        onClose={() => setIsFAQModalOpen(false)}
      />
    </div>
  );
};

export default HomeMobile;
